import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { StandingEntry } from '@/types';

interface StandingsTableProps {
  entries: StandingEntry[];
  /** Всегда компактный вид (виджеты) */
  compact?: boolean;
  onRowClick?: (entry: StandingEntry) => void;
  /** Подсветить свои команды */
  highlightIds?: string[];
}

const diffLabel = (diff?: number) => (diff && diff > 0 ? `+${diff}` : String(diff ?? 0));

export const StandingsTable: React.FC<StandingsTableProps> = ({ entries, compact = false, onRowClick, highlightIds = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [detailed, setDetailed] = useState(false);
  const full = !compact && (!isMobile || detailed);

  const stickyCell = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    minWidth: 120,
  };
  const num = { px: 1, textAlign: 'center' as const, width: 40 };

  return (
    <Paper sx={{ overflow: 'hidden', borderRadius: 0, width: '100%' }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 1, width: 32 }}>№</TableCell>
              <TableCell sx={full ? { ...stickyCell, px: 1 } : { px: 1 }}>Команда</TableCell>
              <TableCell sx={num}>И</TableCell>
              {full && (
                <>
                  <TableCell sx={num}>В</TableCell>
                  <TableCell sx={num}>Н</TableCell>
                  <TableCell sx={num}>П</TableCell>
                  <TableCell sx={{ ...num, width: 64 }}>Мячи</TableCell>
                </>
              )}
              {!compact && <TableCell sx={{ ...num, width: 48 }}>РМ</TableCell>}
              <TableCell sx={num}>О</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => {
              const own = highlightIds.includes(entry.participantId);
              return (
                <TableRow
                  key={entry.participantId}
                  hover={Boolean(onRowClick)}
                  onClick={onRowClick ? () => onRowClick(entry) : undefined}
                  selected={own}
                  sx={{ cursor: onRowClick ? 'pointer' : undefined, height: 44 }}
                >
                  <TableCell sx={{ px: 1 }}>
                    <Typography variant="body2" fontWeight={entry.position <= 3 ? 700 : 400}>
                      {entry.position}
                    </Typography>
                  </TableCell>
                  <TableCell sx={full ? { ...stickyCell, px: 1 } : { px: 1, maxWidth: 160 }}>
                    <Typography variant="body2" noWrap fontWeight={own ? 700 : 400}>
                      {entry.participantName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={num}>{entry.played}</TableCell>
                  {full && (
                    <>
                      <TableCell sx={num}>{entry.won}</TableCell>
                      <TableCell sx={num}>{entry.drawn}</TableCell>
                      <TableCell sx={num}>{entry.lost}</TableCell>
                      <TableCell sx={{ ...num, width: 64 }}>
                        {entry.goalsFor ?? 0}:{entry.goalsAgainst ?? 0}
                      </TableCell>
                    </>
                  )}
                  {!compact && <TableCell sx={{ ...num, width: 48 }}>{diffLabel(entry.goalsDiff)}</TableCell>}
                  <TableCell sx={num}>
                    <b>{entry.points}</b>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {!compact && isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
          <Button size="small" onClick={() => setDetailed((v) => !v)} sx={{ minHeight: 40 }}>
            {detailed ? 'Кратко' : 'Подробно'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
